import Formsy from 'formsy-react';
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';

import useSettings from '../../hooks/useSettings';
import BooleanField from '../../fields/BooleanField';
import DateField from '../../fields/DateField';
/* eslint-disable-next-line import/no-cycle */
import { SearchSelectField, ModuleSearchField, SimpleTextField } from '../../fields';

export interface ReportParameter<T> {
	id: string;
	type?: 'bool' | 'singleValueDate' | 'singleValueDatetime' | 'singleValueText' | 'singleSelect' | string; // FIXME
	label?: string;
	description?: string;
	state: {
		value: T;
		options?: any;
	};
	visible?: boolean;
	readOnly?: boolean;
	mandatory?: boolean;
}

interface ParameterFieldProps {
	parameter: ReportParameter<any>;
	handleChange: (name: string, value: any) => void;
}

interface ParametersStepProps {
	fileName: string;
	setFileName: React.Dispatch<React.SetStateAction<string>>;
	exportFormat: string;
	setExportFormat: React.Dispatch<React.SetStateAction<string>>;
	parameters: ReportParameter<any>[];
	setParameters: React.Dispatch<React.SetStateAction<ReportParameter<any>[]>>;
	setDataValid: React.Dispatch<React.SetStateAction<boolean>>;
}

function ParameterField({ parameter, handleChange }: ParameterFieldProps) {
	const { settings, t } = useSettings(parameter.description);

	if (parameter.type === 'bool') {
		return (
			<Grid size={{ md: 6, xs: 12 }}>
				<BooleanField
					name={parameter.id}
					label={parameter.label}
					value={parameter.state.value === 'true'}
					isEdit={!parameter.readOnly}
					sendChange={(name, newValue) => handleChange(name, `${newValue}`)}
					required={parameter.mandatory}
					validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
				/>
			</Grid>
		);
	}

	if (parameter.type === 'singleValueDate' || parameter.type === 'singleValueDatetime') {
		return (
			<Grid size={{ md: 6, xs: 12 }}>
				<DateField
					name={parameter.id}
					label={parameter.label}
					value={parameter.state.value}
					isEdit={!parameter.readOnly}
					sendChange={handleChange}
					required={parameter.mandatory}
					validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
				/>
			</Grid>
		);
	}

	if ((parameter.type === 'singleValueText' || parameter.type === 'singleValueNumber') && settings) {
		if (settings.modu_systemtable) {
			return (
				<Grid size={12}>
					<SearchSelectField
						name={parameter.id}
						label={parameter.label}
						systable={settings.modu_title}
						value={parameter.state.value}
						isEdit={!parameter.readOnly}
						sendChange={handleChange}
						required={parameter.mandatory}
						validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
					/>
				</Grid>
			);
		}
		return (
			<Grid size={12}>
				<ModuleSearchField
					name={parameter.id}
					label={parameter.label}
					module={settings.modu_title}
					uid={parameter.state.value}
					isEdit={!parameter.readOnly}
					sendChange={handleChange}
					required={parameter.mandatory}
					validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
				/>
			</Grid>
		);
	}

	if (parameter.type === 'singleSelect') {
		return (
			<Grid size={12}>
				<FormControl variant="filled" fullWidth>
					<InputLabel id="format-select-label">
						<Typography variant="subtitle2">{parameter.label}</Typography>
					</InputLabel>
					<Select
						name={parameter.id}
						labelId="format-select-label"
						value={parameter.state.value}
						onChange={e => handleChange(e.target.name, e.target.value)}
						disabled={parameter.readOnly}
						required={parameter.mandatory}
					>
						{parameter.state.options.map((param, k) => (
							<MenuItem value={param.value} key={k}>
								{param.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
		);
	}

	return (
		<Grid size={12}>
			<SimpleTextField
				name={parameter.id}
				label={parameter.label}
				value={parameter.state.value}
				isEdit={!parameter.readOnly}
				sendChange={handleChange}
				required={parameter.mandatory}
				validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
			/>
		</Grid>
	);
}

export default function ParametersStep(props: ParametersStepProps) {
	const { t } = useSettings();

	const exportTypesParam = props.parameters.find(p => p.id === 'EXPORT_TYPES')?.state.options || [
		{ label: 'PDF', value: 'pdf' }
	];

	function handleChange(name, value) {
		props.setParameters(state =>
			state.map(param => {
				if (param.id === name) return { ...param, state: { ...param.state, value } };
				else return param;
			})
		);
	}

	// FIXME: rivedere setDataValid
	return (
		<Formsy onValid={() => props.setDataValid(true)} onInvalid={() => props.setDataValid(false)}>
			<Grid container spacing={1} className="py-8">
				<Grid size={{ md: 8, xs: 12 }}>
					<SimpleTextField
						name="fileName"
						label={t('REPORTING_FIELD_FILENAME')}
						value={props.fileName}
						isEdit
						sendChange={(_name, value) => props.setFileName(value ?? '')}
						required
						validationError={t('ERROR_TEXT_REQUIRED')}
					/>
				</Grid>
				<Grid size={{ md: 4, xs: 12 }}>
					<FormControl variant="filled" fullWidth>
						<InputLabel id="format-select-label">
							<Typography variant="subtitle2">{t('REPORTING_FIELD_FILEFORMAT')}</Typography>
						</InputLabel>
						<Select
							labelId="format-select-label"
							value={props.exportFormat}
							onChange={e => props.setExportFormat(e.target.value)}
						>
							{exportTypesParam.map((param, k) => (
								<MenuItem value={param.value} key={k}>
									{param.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				{props.parameters.map(
					parameter =>
						parameter.visible && (
							<ParameterField key={parameter.id} parameter={parameter} handleChange={handleChange} />
						)
				)}
			</Grid>
		</Formsy>
	);
}
